import React from 'react';
import cx from 'classnames';
import { isNil } from 'lodash';
import styles from './Overview.module.scss';
import { Nexset } from 'new/entities/Nexset';
import DocEditor from 'js/components/elements/editors/DocEditor';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_DATA_SET_DOC_REQUEST } from 'js/constants/actionTypes';
import { NexsetSchemaViewer } from 'new/elements/NexsetSchemaViewer';
import { useResourceDoc } from 'new/hooks/adapters/useResourceDoc';
import { RESOURCE_TYPE_DATA_SET } from 'js/constants/resourceTypes';
import DataSetRecipeTab from 'js/components/data_sets/cards/DataSetRecipeTab';
import { isOperational } from 'new/utils/nexsetUtils';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { getHttpMethodColor, hasEndpointConfiguration } from 'js/lib/ApiServerUtils';
import ApiServerParameterTable from 'js/components/data_sources/cards/configViews/ApiServerParameterTable';
import { FLOW_TYPE_API_SERVER } from 'js/constants/pipelineConstants';
import nexsetTypes from 'new/constants/nexsetTypes';

interface NexsetOverviewProps {
  resource: Nexset;
}

export const NexsetOverview = (props: NexsetOverviewProps) => {
  const { resource } = props;
  const doc = useResourceDoc(RESOURCE_TYPE_DATA_SET, resource?.id);
  const isLoadingDoc = useIsLoading(GET_DATA_SET_DOC_REQUEST);
  const isOperationalNexset = isOperational(resource);
  const isApiServerFlow = resource.flow_type === FLOW_TYPE_API_SERVER;
  const shouldShowDocumentation = !isOperationalNexset;
  const shouldShowRules = isOperationalNexset && !isApiServerFlow;

  if (resource.nexset_type === nexsetTypes.api_endpoint) {
    const { endpoint_spec: endpointSpec } = resource;

    return (
      <>
        {endpointSpec && (
          <Stack gap="16px" padding="16px">
            <Stack gap="8px" direction="row" alignItems="center">
              <Chip
                label={endpointSpec.method}
                color={getHttpMethodColor(endpointSpec.method)}
                variant="outlined"
              />
              <Typography variant="body2">{endpointSpec.route || 'No route configured'}</Typography>
            </Stack>
            {hasEndpointConfiguration(endpointSpec) ? (
              <Stack gap="8px">
                <ApiServerParameterTable params={endpointSpec.headers || []} title="Headers" />
                <ApiServerParameterTable
                  params={endpointSpec.path_params || []}
                  title="Path Parameters"
                />
                <ApiServerParameterTable
                  params={endpointSpec.query_params || []}
                  title="Query Parameters"
                />
                <ApiServerParameterTable
                  body={endpointSpec.body}
                  title="Request Body"
                  subtitle="application/json"
                />
              </Stack>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body2">No configured endpoint specification</Typography>
              </Box>
            )}
          </Stack>
        )}
      </>
    );
  }

  return (
    <div className={cx(styles.overviewContainer, styles.nexsetOverviewContainer, 'detail-view')}>
      <div className="tab-content">
        {shouldShowDocumentation && !isNil(doc) && !isLoadingDoc ? (
          <DocEditor data={resource} doc={doc} readOnly isLoading={isLoadingDoc} />
        ) : null}
        {shouldShowRules ? (
          <DataSetRecipeTab data={resource} />
        ) : (
          <NexsetSchemaViewer data={resource} hideTitle />
        )}
      </div>
    </div>
  );
};
